import Accordion from '../Accordion/Accordion';
import React, { FC, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  SxProps,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import styled from 'styled-components';
import BenchmarkDropzone from '../BenchmarkDropzone/BenchmarkDropzone';
import { IBenchmark } from '../../shared/interfaces/IBenchmark';
import { useTranslation } from 'react-i18next';
import BenchmarkCompetitor from '../BenchmarkCompetitor/BenchmarkCompetitor';
import BenchmarkDataCell from '../BenchmarkDataCell/BenchmarkDataCell';
import DeleteIcon from '@mui/icons-material/Delete';
import { constants } from '../../constants/constants';
import Confirm from '../Confirm/Confirm';

const AccordionSummaryLogo = styled.img`
  max-height: 38px;
  max-width: 76px;
`;

interface Props extends IBenchmark {
  onDelete: (id: string) => Promise<void>;
  reload: () => Promise<void>;
}

const Benchmark: FC<Props> = ({
  id,
  label,
  platform,
  subject,
  competition,
  onDelete,
  reload,
  createdAt,
  updatedAt,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [isExpanded, setIsExpanded] = useState(false);
  const [deleteIsOpen, setDeleteIsOpen] = useState(false);
  const [subID, setSubID] = useState(subject.id ? subject.id : '');

  const borderProps: SxProps<Theme> = {
    borderBottom: `1px solid ${theme.palette.divider}`,
  };

  if (platform === 'Ärzte') {
    platform = 'aerzte';
  }
  if (platform === 'Mobile.de') {
    platform = 'mobile';
  }

  useEffect(() => {
    if (!subject.id) return;

    setSubID(subject.id);
  }, [subject.id]);

  return (
    <Accordion
      expanded={isExpanded}
      onExpandRequest={() => setIsExpanded((prevState) => !prevState)}
      summary={
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
          }}
        >
          <AccordionSummaryLogo src={constants.platform_logos[platform.toLowerCase()]} />
          <Tooltip
            title={subject.company}
            arrow
          >
            <Typography
              variant="body2"
              sx={{ ml: theme.spacing(2), cursor: 'pointer', fontWeight: 700 }}
            >
              {subject.label.slice(0, 18)}
            </Typography>
          </Tooltip>
          {competition.map((c) => {
            return (
              <Tooltip
                key={c.id}
                title={c.company}
                arrow
              >
                <Chip
                  label={c.label.slice(0, 18)}
                  size="small"
                  sx={{
                    ml: theme.spacing(1),
                    cursor: 'pointer',
                  }}
                />
              </Tooltip>
            );
          })}
          <IconButton
            color="error"
            size="small"
            onClick={() => setDeleteIsOpen(true)}
            sx={{ ml: 'auto' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
          <Confirm
            text={t('delete_confirm')}
            open={deleteIsOpen}
            onCancel={() => setDeleteIsOpen(false)}
            onOk={async () => await onDelete(id)}
          />
        </Box>
      }
      details={
        <Box sx={{ display: 'flex' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              px: theme.spacing(2),
              minWidth: 260,
            }}
          >
            <BenchmarkDataCell
              sx={{
                borderBottom: `1px solid ${theme.palette.divider}`,
                alignItems: 'center',
              }}
            >
              <Typography variant="body1">&nbsp;</Typography>
              <Box sx={{ height: 48 }} />
            </BenchmarkDataCell>
            <BenchmarkDataCell sx={borderProps}>
              <Typography variant="body1">{t('score')}</Typography>
            </BenchmarkDataCell>
            <BenchmarkDataCell sx={borderProps}>
              <Typography variant="body1">{t('review_count')}</Typography>
            </BenchmarkDataCell>
            <BenchmarkDataCell sx={borderProps}>
              <Typography variant="body1">{t('company_response')}</Typography>
            </BenchmarkDataCell>
            <BenchmarkDataCell sx={borderProps}>
              <Typography variant="body1">{t('development_week')}</Typography>
            </BenchmarkDataCell>
            <BenchmarkDataCell sx={borderProps}>
              <Typography variant="body1">{t('month')}</Typography>
            </BenchmarkDataCell>
            <BenchmarkDataCell>
              <Typography variant="body1">{t('year')}</Typography>
            </BenchmarkDataCell>
          </Box>
          <BenchmarkCompetitor
            label={subject.label}
            company={subject.company}
            url={subject.url}
            index={1}
            isSubject={true}
            platform={platform.toLowerCase()}
            sx={{ backgroundColor: 'rgba(53,126,139,0.1)' }}
            id={id}
            competitorID={subject.id}
            reload={reload}
          />
          {competition.map((c) => {
            return (
              <BenchmarkCompetitor
                key={c.id}
                label={c.label}
                company={c.company}
                url={c.url}
                index={competition.indexOf(c) + 2}
                isSubject={false}
                platform={platform.toLowerCase()}
                benchmarkID={id}
                competitorID={c.id}
                reload={reload}
              />
            );
          })}
          <Box sx={{ display: 'flex' }}>
            {competition.length <= 3 && (
              <BenchmarkDropzone
                benchmarkID={subID}
                subject={subject}
                companyUrl={subject.url}
                competitors={competition}
                label={label}
                id={id}
                platform={platform}
                reload={reload}
              />
            )}
          </Box>
        </Box>
      }
    />
  );
};

export default Benchmark;
